import React, { useState } from 'react';
import { getAuth, createUserWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, query, collection, where, getDocs, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { app, db, googleProvider } from '../firebaseConfig';
import { useNavigate, Link } from 'react-router-dom';
import { FaGoogle, FaEnvelope, FaLock, FaPhone, FaCheckCircle } from 'react-icons/fa';
import { trackFunnelEvent } from '../utils/AnalyticsService';

const auth = getAuth(app);

const sanitizeFirebaseError = (error, prefix = '') => {
  const msg = error?.message || '';
  if (msg.includes('permission-denied') || msg.includes('suspended') || msg.includes('api-key') || msg.includes('API key')) {
    return '🔧 System Maintenance: We are performing a temporary security upgrade. Registration service will be restored shortly. Thank you for your patience!';
  }
  const cleanMsg = msg.replace(/api-key:[a-zA-Z0-9_-]+/gi, 'api-key:[restricted]').replace('Firebase: ', '');
  return prefix ? `${prefix}: ${cleanMsg}` : cleanMsg;
};

const translations = {
  en: {
    gateway: "Your gateway to global opportunities.",
    joinThousands: "Join thousands of professionals scoring 65+ on their English Fluency exams using our AI Simulator.",
    aiGrading: "Realistic Human-Level AI Grading",
    fluencyAnalytics: "Detailed Fluency Analytics",
    freeTest: "Instantly Unlock Test 1 Free",
    createAccount: "Create an account",
    startMock: "Start your free mock test journey today.",
    signUpGoogle: "Sign up with Google",
    orEmail: "Or with email",
    mobileNumber: "Mobile Number",
    emailAddress: "Email Address",
    passwordMin: "Password (min 6 chars)",
    alreadyHaveAccount: "Already have an account?",
    logIn: "Log In"
  },
  es: {
    gateway: "Tu puerta de entrada a oportunidades globales.",
    joinThousands: "Únete a miles de profesionales que obtienen más de 65 puntos en sus exámenes de fluidez en inglés usando nuestro simulador de IA.",
    aiGrading: "Calificación de IA realista a nivel humano",
    fluencyAnalytics: "Análisis detallados de fluidez",
    freeTest: "Desbloquea instantáneamente el Examen 1 gratis",
    createAccount: "Crear una cuenta",
    startMock: "Comienza tu viaje de simulacro gratuito hoy.",
    signUpGoogle: "Registrarse con Google",
    orEmail: "O con correo electrónico",
    mobileNumber: "Número de teléfono móvil",
    emailAddress: "Correo electrónico",
    passwordMin: "Contraseña (mínimo 6 caracteres)",
    alreadyHaveAccount: "¿Ya tienes una cuenta?",
    logIn: "Iniciar sesión"
  },
  ja: {
    gateway: "グローバルな機会へのゲートウェイ。",
    joinThousands: "AIシミュレーターを使用して、英語流暢性試験で65点以上を獲得している何千人ものプロフェッショナルに加わりましょう。",
    aiGrading: "人間レベルのリアルなAI採点",
    fluencyAnalytics: "詳細な流暢性分析",
    freeTest: "テスト1を今すぐ無料でアンロック",
    createAccount: "アカウント作成",
    startMock: "今すぐ無料模擬テストを始めましょう。",
    signUpGoogle: "Googleで登録",
    orEmail: "またはメールアドレスで登録",
    mobileNumber: "携帯電話番号",
    emailAddress: "メールアドレス",
    passwordMin: "パスワード（6文字以上）",
    alreadyHaveAccount: "すでにアカウントをお持ちですか？",
    logIn: "ログイン"
  },
  pt: {
    gateway: "Seu portal para oportunidades globais.",
    joinThousands: "Junte-se a milhares de profissionais que alcançam mais de 65 pontos em seus exames de fluência em inglês usando nosso simulador de IA.",
    aiGrading: "Correção de IA realista em nível humano",
    fluencyAnalytics: "Análise detalhada de fluência",
    freeTest: "Desbloqueie instantaneamente o Teste 1 grátis",
    createAccount: "Criar uma conta",
    startMock: "Comece sua jornada de simulado gratuito hoje.",
    signUpGoogle: "Cadastrar-se com o Google",
    orEmail: "Ou com e-mail",
    mobileNumber: "Número de celular",
    emailAddress: "Endereço de e-mail",
    passwordMin: "Senha (mínimo 6 caracteres)",
    alreadyHaveAccount: "Já tem uma conta?",
    logIn: "Entrar"
  }
};

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const navigate = useNavigate();

  const locale = sessionStorage.getItem('versant_locale') || 'en';
  const t = translations[locale] || translations.en;

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const lowerEmail = user.email.toLowerCase().trim();

      // Check if they have guest ebook purchases
      let hasPurchasedEbook = false;
      let ebookPurchasedAt = null;
      let ebookTransactionId = '';

      try {
        const ebookQuery = query(
          collection(db, 'ebook_purchases'),
          where('email', '==', lowerEmail),
          where('status', '==', 'success')
        );
        const querySnapshot = await getDocs(ebookQuery);
        if (!querySnapshot.empty) {
          hasPurchasedEbook = true;
          const docData = querySnapshot.docs[0].data();
          ebookPurchasedAt = docData.paidAt || null;
          ebookTransactionId = querySnapshot.docs[0].id;

          // Link uid to the purchase document
          await updateDoc(doc(db, 'ebook_purchases', ebookTransactionId), {
            uid: user.uid
          });
        }
      } catch (err) {
        console.error("Error looking up guest ebook purchases during signup:", err);
      }

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: lowerEmail,
        mobileNumber: mobileNumber,
        hasPaid: false,
        referredBy: localStorage.getItem('versant_affiliate_ref') || null,
        hasPurchasedEbook: hasPurchasedEbook,
        ...(ebookPurchasedAt ? { ebookPurchasedAt } : {}),
        ...(ebookTransactionId ? { ebookTransactionId } : {})
      });

      // Sync any guest test result taken before signup
      const pendingResultRaw = sessionStorage.getItem('pending_guest_result');
      if (pendingResultRaw) {
        try {
          const { resultData } = JSON.parse(pendingResultRaw);
          await addDoc(collection(db, 'users', user.uid, 'testResults'), {
            ...resultData,
            timestamp: new Date().toISOString(),
            createdAt: serverTimestamp()
          });
          sessionStorage.removeItem('pending_guest_result');
        } catch (e) {
          console.error("Error saving pending guest result on signup:", e);
        }
      }

      await trackFunnelEvent('user_registered', { method: 'email' });
      navigate('/dashboard');
    } catch (error) {
      alert(sanitizeFirebaseError(error));
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if Firestore user doc exists, otherwise create it
      const userRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(userRef);
      if (!docSnap.exists()) {
        const lowerEmail = user.email.toLowerCase().trim();
        let hasPurchasedEbook = false;
        let ebookPurchasedAt = null;
        let ebookTransactionId = '';

        try {
          const ebookQuery = query(
            collection(db, 'ebook_purchases'),
            where('email', '==', lowerEmail),
            where('status', '==', 'success')
          );
          const querySnapshot = await getDocs(ebookQuery);
          if (!querySnapshot.empty) {
            hasPurchasedEbook = true;
            const docData = querySnapshot.docs[0].data();
            ebookPurchasedAt = docData.paidAt || null;
            ebookTransactionId = querySnapshot.docs[0].id;

            // Link uid to purchase document
            await updateDoc(doc(db, 'ebook_purchases', ebookTransactionId), {
              uid: user.uid
            });
          }
        } catch (err) {
          console.error("Error looking up guest purchases during Google signup:", err);
        }

        await setDoc(userRef, {
          uid: user.uid,
          email: lowerEmail,
          name: user.displayName,
          hasPaid: false,
          referredBy: localStorage.getItem('versant_affiliate_ref') || null,
          hasPurchasedEbook: hasPurchasedEbook,
          ...(ebookPurchasedAt ? { ebookPurchasedAt } : {}),
          ...(ebookTransactionId ? { ebookTransactionId } : {})
        });
        await trackFunnelEvent('user_registered', { method: 'google' });
      } else {
        await trackFunnelEvent('user_login', { method: 'google' });
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Google Sign-In Error:', error.message);
      alert(sanitizeFirebaseError(error, 'Google Sign-In Failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fade-in relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-400/20 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-400/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] overflow-hidden flex flex-col md:flex-row border border-gray-100 relative z-10">
        
        {/* Left Side: Marketing/Branding (Hidden on mobile) */}
        <div className="hidden md:flex md:w-5/12 bg-gradient-to-br from-emerald-600 via-teal-700 to-indigo-900 p-12 text-white flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 right-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          
          <div className="relative z-10">
             <Link to="/" className="flex items-center space-x-3 mb-10 hover:opacity-80 transition-opacity w-max">
               <div className="w-10 h-10 bg-white text-emerald-600 rounded-full flex items-center justify-center font-black text-xl shadow-lg">V</div>
               <span className="text-2xl font-black tracking-tight">VersantPro</span>
             </Link>
             
             <h2 className="text-4xl font-black mb-6 leading-tight">{t.gateway}</h2>
             <p className="text-emerald-100 text-lg mb-8 font-medium">{t.joinThousands}</p>
             
             <ul className="space-y-4">
                <li className="flex items-center gap-3 font-medium"><FaCheckCircle className="text-emerald-300 text-xl" /> {t.aiGrading}</li>
                <li className="flex items-center gap-3 font-medium"><FaCheckCircle className="text-emerald-300 text-xl" /> {t.fluencyAnalytics}</li>
                <li className="flex items-center gap-3 font-medium"><FaCheckCircle className="text-emerald-300 text-xl" /> {t.freeTest}</li>
             </ul>
          </div>
          
          <div className="relative z-10 text-sm font-medium text-emerald-200">
             © {new Date().getFullYear()} VersantPro Simulator
          </div>
        </div>

        {/* Right Side: Auth Form */}
        <div className="w-full md:w-7/12 p-8 sm:p-12 lg:p-16 flex flex-col justify-center bg-white">
          <div className="max-w-md mx-auto w-full">
            <h2 className="text-3xl font-black text-gray-900 mb-2">{t.createAccount}</h2>
            <p className="text-gray-500 mb-8 font-medium">{t.startMock}</p>

            {/* Google Signup */}
            <button 
              type="button"
              onClick={handleGoogleSignup}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 font-bold py-3.5 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm active:scale-95 mb-6"
            >
              <FaGoogle className="text-red-500 text-xl" />
              {t.signUpGoogle}
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">{t.orEmail}</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            <form onSubmit={handleSignup} className="space-y-5">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <FaPhone />
                </div>
                <input
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  placeholder={t.mobileNumber}
                  className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all font-medium text-gray-900"
                  pattern="[0-9]{10}"
                  title="Mobile number should be 10 digits"
                  required
                />
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <FaEnvelope />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailAddress}
                  className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all font-medium text-gray-900"
                  required
                />
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <FaLock />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passwordMin}
                  className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all font-medium text-gray-900"
                  minLength="6"
                  required
                />
              </div>

              <button type="submit" className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-gray-900/30 transition-all active:scale-95 text-lg">
                {t.createAccount}
              </button>
            </form>

            <p className="mt-8 text-center text-gray-600 font-medium">
              {t.alreadyHaveAccount}{' '}
              <Link to="/login" className="text-emerald-600 font-bold hover:text-emerald-700 hover:underline">
                {t.logIn}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;